// Hasta çalışma alanı "İlaç Alarmları" sekmesi — düz liste | editör.
// Solda uyum özeti şeridi + kaynağa göre gruplu planlar, sağda plan editörü.
// Su takibi ayrı sekmede (HydrationTab). Kurallar: docs/design-principles.md §8.3

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Chip, Skeleton, Typography, alpha } from "@mui/material";
import { Alarm as AlarmIcon, ChevronRight as ChevronIcon } from "@mui/icons-material";

import SelectableListRow from "../../molecules/SelectableListRow";
import { ActionButton } from "../../atoms";
import ConfirmDialog from "../../common/ConfirmDialog";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import { useHasPermission } from "../../../hooks/useHasPermission";
import {
  useListMedicationPlansMutation,
  useCreateMedicationPlansMutation,
  useUpdateMedicationPlanMutation,
  usePauseMedicationPlanMutation,
  useResumeMedicationPlanMutation,
  useEndMedicationPlanMutation,
  useMedicationAdherenceMutation,
} from "../../../store/medicationPlanApi";
import MedicationPlanEditor from "./MedicationPlanEditor";
import AdherenceDrawer, { adherenceTone } from "./drawers/AdherenceDrawer";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  CreatingRow,
  ListGroupLabel,
} from "./_workspaceParts";
import { formToCreateBody, formToPatch } from "./medicationPlanForm";
import useTerms from "../../../hooks/useTerms";

const DOSE_UNIT_SUFFIX = (p) => `${p.dose} ${p.doseUnit}`;

/** Listenin başındaki uyum özeti (son 7 gün); tıklayınca ayrıntı drawer'ı. */
function AdherenceStrip({ summary, loading, onOpen, palette }) {
  const tone = adherenceTone(summary?.percent);
  const noDose = !loading && (!summary || summary.expected === 0);
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label="İlaç uyumu ayrıntısı"
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        textAlign: "left",
        pl: { xs: 2, md: 4 },
        pr: 2,
        py: 1.5,
        borderBottom: `1px solid ${COLORS.slate200}`,
        "&:hover": { bgcolor: alpha(palette.primary, 0.03) },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: TYPO.caption,
            fontWeight: 600,
            color: COLORS.slate500,
            textTransform: "uppercase",
            letterSpacing: 0.6,
          }}
        >
          Uyum · son 7 gün
        </Typography>
        {loading ? (
          <Skeleton width="60%" height={22} />
        ) : noDose ? (
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate500, mt: 0.25 }}>
            Henüz vadesi gelen doz yok
          </Typography>
        ) : (
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate600, mt: 0.25 }}>
            <Box
              component="span"
              sx={{ fontWeight: 700, color: tone, fontVariantNumeric: "tabular-nums", mr: 0.75 }}
            >
              %{summary.percent}
            </Box>
            {summary.missed ? `${summary.missed} doz kaçırıldı` : "Kaçırılan doz yok"}
          </Typography>
        )}
      </Box>
      <ChevronIcon sx={{ fontSize: 20, color: COLORS.slate400 }} />
    </ButtonBase>
  );
}

export default function MedicationAlarmsTab({ patientId, token, canEdit: canEditProp = true }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const canManage = useHasPermission("medication-plans:manage") && canEditProp;

  // ── Sekme içi bildirim (diğer çalışma alanı sekmeleriyle aynı) ────
  const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });
  const alertTimer = useRef(null);
  const notify = useCallback((message, severity = "success") => {
    if (alertTimer.current) clearTimeout(alertTimer.current);
    setAlert({ open: true, severity, message });
    alertTimer.current = setTimeout(
      () => setAlert((a) => ({ ...a, open: false })),
      severity === "error" ? 5000 : 3500
    );
  }, []);
  useEffect(() => () => alertTimer.current && clearTimeout(alertTimer.current), []);

  // ── Veri ──────────────────────────────────────────────────────────
  const [list, { data, isLoading, isUninitialized }] = useListMedicationPlansMutation();
  const [loadAdherence, { data: adherenceData, isLoading: adherenceLoading }] =
    useMedicationAdherenceMutation();
  const [create, { isLoading: creating }] = useCreateMedicationPlansMutation();
  const [update, { isLoading: updating }] = useUpdateMedicationPlanMutation();
  const [pause] = usePauseMedicationPlanMutation();
  const [resume] = useResumeMedicationPlanMutation();
  const [end, { isLoading: ending }] = useEndMedicationPlanMutation();

  const [selectedId, setSelectedId] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [adherenceOpen, setAdherenceOpen] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  // "Vazgeç" düzenlemeyi kayıttaki hale döndürür (editör yeniden kurulur)
  const [editorNonce, setEditorNonce] = useState(0);

  const reload = useCallback(() => {
    if (!token || !patientId) return;
    list({ token, patientId });
    loadAdherence({ token, patientId, days: 7 });
    setRefreshKey((k) => k + 1);
  }, [token, patientId, list, loadAdherence]);
  useEffect(() => {
    reload();
  }, [reload]);

  const plans = useMemo(() => data?.data?.list || [], [data]);
  const clinicPlans = plans.filter((p) => p.source === "clinic");
  const patientPlans = plans.filter((p) => p.source === "patient");
  const selected = plans.find((p) => p._id === selectedId) || null;
  const loaded = Boolean(data) || !(isLoading || isUninitialized);

  // İlk yüklemede ilk planı aç (Diyet/Reçete sekmeleriyle aynı)
  useEffect(() => {
    if (!isNew && !selectedId && plans.length) setSelectedId(plans[0]._id);
  }, [plans, isNew, selectedId]);

  const run = async (fn, okText) => {
    try {
      await fn();
      notify(okText);
      reload();
    } catch (e) {
      notify(e?.data?.message || "İşlem başarısız", "error");
    }
  };

  const onSave = (form) =>
    isNew
      ? run(async () => {
          const r = await create({ token, patientId, ...formToCreateBody(form) }).unwrap();
          setIsNew(false);
          setSelectedId(r?.data?.plans?.[0]?._id || null);
        }, "Alarm planı oluşturuldu")
      : run(
          () => update({ token, planId: selected._id, patch: formToPatch(selected, form) }).unwrap(),
          "Plan güncellendi"
        );

  const startNew = () => {
    setIsNew(true);
    setSelectedId(null);
  };
  const cancelNew = () => {
    setIsNew(false);
    setSelectedId(plans[0]?._id || null);
  };

  // Klinik, hastanın kendi eklediği planı yalnız görür
  const locked = selected?.source === "patient" ? ["*"] : [];

  const planActions =
    !isNew && selected && canManage && selected.source === "clinic" ? (
      <Box sx={{ display: "flex", gap: 1 }}>
        {selected.status === "active" ? (
          <ActionButton
            variant="edit"
            tone="outline"
            compact
            label="Duraklat"
            onClick={() => run(() => pause({ token, planId: selected._id }).unwrap(), "Plan duraklatıldı")}
          />
        ) : (
          <ActionButton
            variant="confirm"
            tone="soft"
            compact
            label="Devam ettir"
            palette={palette}
            onClick={() => run(() => resume({ token, planId: selected._id }).unwrap(), "Plan devam ediyor")}
          />
        )}
        <ActionButton variant="delete" tone="soft" compact label="Sonlandır" onClick={() => setConfirmEnd(true)} />
      </Box>
    ) : null;

  const renderRow = (p) => {
    const paused = p.status !== "active";
    return (
      <SelectableListRow
        key={p._id}
        active={p._id === selectedId}
        onClick={() => {
          setSelectedId(p._id);
          setIsNew(false);
        }}
        sx={{ ...LIST_ROW_SX, borderBottom: `1px solid ${COLORS.slate100}` }}
        icon={<AlarmIcon sx={{ fontSize: 18, color: paused ? COLORS.slate400 : palette.primary }} />}
        title={p.name}
        subtitle={`${DOSE_UNIT_SUFFIX(p)} · ${p.schedule.times.join(", ")}`}
        trailing={
          paused ? (
            <Chip
              size="small"
              label="Duraklatıldı"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 600,
                borderRadius: 999,
                bgcolor: COLORS.slate100,
                color: COLORS.slate600,
              }}
            />
          ) : null
        }
      />
    );
  };

  const editor = isNew ? (
    <MedicationPlanEditor
      plan={null}
      canEdit={canManage}
      saving={creating}
      onSave={onSave}
      onCancel={cancelNew}
    />
  ) : selected ? (
    <MedicationPlanEditor
      key={`${selected._id}-${editorNonce}`}
      plan={selected}
      canEdit={canManage}
      lockedFields={locked}
      saving={updating}
      onSave={onSave}
      onCancel={() => setEditorNonce((n) => n + 1)}
      actions={planActions}
    />
  ) : (
    <SelectPrompt
      icon={<AlarmIcon />}
      title="Bir plan seçin"
      description="Ayrıntılarını görmek için soldaki listeden bir alarm planı seçin."
    />
  );

  return (
    <>
      <Box sx={WORKSPACE_ROOT_SX}>
        <WorkspaceBody
          title="İlaç Alarmları"
          count={plans.length}
          action={
            canManage && plans.length > 0 ? (
              <ActionButton variant="add" compact label="Yeni Plan" palette={palette} onClick={startNew} disabled={isNew} />
            ) : null
          }
          alert={alert}
          onAlertClose={() => setAlert((a) => ({ ...a, open: false }))}
        >
          {!loaded ? (
            <WorkspaceSkeleton />
          ) : plans.length === 0 && !isNew ? (
            <WorkspaceEmpty
              icon={<AlarmIcon />}
              title="Henüz alarm planı yok"
              lead={`${terms.patientGen} telefonu doz saatinde alarm çalsın diye`}
              linkLabel="alarm planı oluşturun"
              tail="Reçete yazarken de plan önerilir."
              readOnlyText={`Bu ${terms.patientLower} için aktif veya duraklatılmış alarm planı bulunmuyor.`}
              onCreate={canManage ? startNew : undefined}
              palette={palette}
            />
          ) : (
            <WorkspaceSplit
              list={
                <>
                  <AdherenceStrip
                    summary={adherenceData?.data}
                    loading={adherenceLoading && !adherenceData}
                    onOpen={() => setAdherenceOpen(true)}
                    palette={palette}
                  />
                  {isNew ? (
                    <CreatingRow
                      label="Yeni alarm planı"
                      hint="Kaydedince listeye eklenir"
                      onCancel={cancelNew}
                      palette={palette}
                    />
                  ) : null}
                  {clinicPlans.length > 0 ? (
                    <>
                      <ListGroupLabel first count={clinicPlans.length}>
                        Klinik planları
                      </ListGroupLabel>
                      {clinicPlans.map(renderRow)}
                    </>
                  ) : null}
                  {patientPlans.length > 0 ? (
                    <>
                      <ListGroupLabel first={clinicPlans.length === 0} count={patientPlans.length}>
                        {terms.patientGen} ekledikleri
                      </ListGroupLabel>
                      {patientPlans.map(renderRow)}
                    </>
                  ) : null}
                </>
              }
              editor={editor}
            />
          )}
        </WorkspaceBody>
      </Box>

      <AdherenceDrawer
        open={adherenceOpen}
        onClose={() => setAdherenceOpen(false)}
        patientId={patientId}
        token={token}
        refreshKey={refreshKey}
      />

      <ConfirmDialog
        open={confirmEnd}
        onClose={() => setConfirmEnd(false)}
        onConfirm={() =>
          run(async () => {
            await end({ token, planId: selected._id }).unwrap();
            setConfirmEnd(false);
            setSelectedId(null);
          }, "Plan sonlandırıldı")
        }
        title="Planı Sonlandır"
        message={`“${selected?.name || ""}” planı sonlandırılacak ve ${terms.patientGenLower} telefonundaki alarmlar duracak. Bu işlem geri alınamaz.`}
        confirmText="Sonlandır"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={ending}
      />
    </>
  );
}
